import ExtensionResultStore from 'stores/extensionResult/ExtensionResultStore';
import BaseInstanceListStore from 'stores/BaseInstanceListStore';
import { IDataFetchOptions } from '../../types';
import getAnalysisContext from '../../helpers/getAnalysisContext';
import { getConditions, getListConditions } from 'stores/_services/filterService';
import { buildOptions, get, getPage } from 'stores/_services/dataService';
import { action, makeObservable } from 'mobx';
import { buildCsvDownloadUrl } from 'stores/_services/downloadService';
import { SortDirection } from 'constants/SortDirection';

const SEARCH_FIELDS = ['name', 'sha256', 'outputString', 'hostname'];

interface IGetExtensionResultsArgs extends IDataFetchOptions {
    hostId?: string;
    hostScanId?: string;
    extensionId?: string;
}

export default class ExtensionResultListStore extends BaseInstanceListStore<ExtensionResultStore> {
    constructor() {
        super({
            itemStore: ExtensionResultStore,
            sortBy: 'scannedOn',
            sortDirection: SortDirection.DESC,
            baseRoute: 'scanExtensionInstances'
        });

        makeObservable(this, {
            getExtensionResults: action,
            getExtensionResult: action
        });
    }

    async getExtensionResults({ hostId, hostScanId, extensionId, ...args }: IGetExtensionResultsArgs = {}): Promise<void> {
        const { analysisFilter } = await getAnalysisContext();
        const shouldReset = this._updateListState(args);

        const conditions: any[] = getConditions(this.filter);

        if (analysisFilter) {
            conditions.push(analysisFilter);
        }

        if (hostId) {
            conditions.push({ hostId });
        } else if (hostScanId) {
            conditions.push({ hostScanId });
        } else if (extensionId) {
            conditions.push({ extensionId });
        }

        const options = {
            count: !this.count || shouldReset,
            filter: {
                limit: this.limit,
                skip: this.skip,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            }
        };

        const { data } = await getPage(this.baseRoute, options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);
    }

    async getExtensionResult(extensionResultId: string): Promise<void> {
        const route = 'extensionResults';
        const options = {
            filter: {
                include: [
                    {
                        relation: 'extensionVersion',
                        scope: {
                            fields: ['sha256']
                        }
                    }
                ]
            }
        };

        const { data } = await get(`${route}/${extensionResultId}`, options);
        this._updateData(data);
    }

    public async exportToCsv(extensionId: string): Promise<void> {
        const { boxId, analysisFilter } = await getAnalysisContext();
        const conditions = getListConditions(this.selectedIdList, this.filter);

        conditions.push({ extensionId });

        if (analysisFilter) {
            conditions.push(analysisFilter);
        }

        if (boxId) {
            conditions.push({ boxId });
        }

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            },
            excludeFields: ['fileRepId', 'id', 'flagId', 'boxId']
        };

        const query = buildOptions(options);

        window.location.href = await buildCsvDownloadUrl(this.baseRoute, query);
    }
}
